import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface ProjectRequest {
  id: string;
  freelancer_id: string;
  freelancer_name: string;
  freelancer_email: string;
  message: string;
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn';
  proposed_delivery_date?: string;
  proposed_price?: number;
  created_at: string;
  freelancer?: {
    id: string;
    name: string;
    email: string;
    username: string;
    whatsapp_contact?: string;
  };
}

interface Project {
  id: string;
  title: string;
  budget: number;
  status: string;
}

const ProjectRequests: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ProjectRequest[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);

  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        if (parsedUser.role !== 'client') {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  const loadProjectRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // Load project details
      const projectResponse = await axios.get(
        `http://localhost:5000/api/projects/${projectId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (projectResponse.data.success) {
        setProject(projectResponse.data.data.project);
      }

      // Load requests
      const requestsResponse = await axios.get(
        `http://localhost:5000/api/projects/${projectId}/requests`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (requestsResponse.data.success) {
        setRequests(requestsResponse.data.data.requests);
      }
    } catch (error) {
      console.error('Error loading project requests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (user && projectId) {
      loadProjectRequests();
    }
  }, [user, projectId, loadProjectRequests]);

  const handleAssignProject = async (freelancerId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to assign this project to this freelancer?')) {
      return;
    }

    try {
      setIsAssigning(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/assign/${freelancerId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        alert('Project assigned successfully!');
        navigate(`/client/projects/${projectId}`);
      }
    } catch (error: any) {
      console.error('Error assigning project:', error);
      alert(error.response?.data?.message || 'Error assigning project');
    } finally {
      setIsAssigning(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'withdrawn': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading project requests...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
            <p className="text-gray-600 mb-6">The requested project could not be found.</p>
            <button
              onClick={() => navigate('/client/projects')}
              className="btn btn-primary"
            >
              Back to Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'C'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.username?.toUpperCase()}</span>
                <div className="user-meta">
                  <span className="role-badge role-client">Client</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Project Requests: {project.title}
              </h1>
              <p className="text-gray-600">
                Review and assign freelancers to your project
              </p>
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={() => navigate(`/client/projects/${projectId}`)}
                className="btn btn-secondary"
              >
                ← Back to Project
              </button>
              <button 
                onClick={() => navigate('/client/projects')}
                className="btn btn-secondary"
              >
                All Projects
              </button>
            </div>
          </div>

          {/* Project Info */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-600 mb-1">Project Budget</div>
                <div className="text-2xl font-bold text-green-600">₦{project.budget.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Project Status</div>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  project.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  project.status === 'active' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {project.status}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Total Requests</div>
                <div className="text-2xl font-bold text-blue-600">{requests.length}</div>
                <div className="text-sm text-gray-600">
                  {pendingRequests.length} pending
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Requests List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Freelancer Requests ({pendingRequests.length} pending)
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Review each request and select the most suitable freelancer for your project
            </p>
          </div>

          {requests.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No requests yet</h3>
              <p className="text-gray-600 mb-6">
                Freelancers haven't requested to work on this project yet. Check back later.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {requests.map((request) => (
                <div key={request.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    {/* Freelancer Info */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-bold text-lg">
                            {request.freelancer_name?.charAt(0).toUpperCase() || 'F'}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {request.freelancer_name}
                          </h3>
                          <div className="flex items-center space-x-3 text-sm text-gray-600">
                            <span>{request.freelancer_email}</span>
                            {request.freelancer?.whatsapp_contact && (
                              <span>📱 {request.freelancer.whatsapp_contact}</span>
                            )}
                          </div>
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                              {request.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Message */}
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Request Message:</h4>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-gray-700 whitespace-pre-line">{request.message}</p>
                        </div>
                      </div>

                      {/* Proposal Details */}
                      {(request.proposed_price || request.proposed_delivery_date) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          {request.proposed_price && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Proposed Price</h4>
                              <div className="text-lg font-bold text-green-600">
                                ₦{request.proposed_price.toLocaleString()}
                              </div>
                              <div className="text-sm text-gray-600">
                                {request.proposed_price > project.budget ? 'Above budget' : 'Within budget'}
                              </div>
                            </div>
                          )}
                          {request.proposed_delivery_date && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Proposed Delivery</h4>
                              <div className="text-lg font-bold text-blue-600">
                                {formatDate(request.proposed_delivery_date)}
                              </div>
                              <div className="text-sm text-gray-600">
                                Suggested timeline
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Request Date */}
                      <div className="text-sm text-gray-500">
                        Request submitted on {formatDate(request.created_at)}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="ml-6">
                      {request.status === 'pending' ? (
                        <div className="space-y-2">
                          <button
                            onClick={() => handleAssignProject(request.freelancer_id)}
                            disabled={isAssigning}
                            className="btn btn-primary w-full"
                          >
                            {isAssigning ? 'Assigning...' : 'Assign Project'}
                          </button>
                          <button
                            onClick={() => navigate(`/messages/${projectId}`)}
                            className="btn btn-secondary w-full"
                          >
                            Message Freelancer
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className={`px-3 py-2 rounded-lg font-medium ${getStatusColor(request.status)}`}>
                            {request.status.toUpperCase()}
                          </div>
                          {request.status === 'accepted' && (
                            <div className="text-sm text-green-600 mt-2">
                              Project assigned
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help Text */}
        {pendingRequests.length > 0 && (
          <div className="mt-6 bg-blue-50 p-4 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Tips for selecting a freelancer:</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Review the freelancer's message and proposed terms carefully</li>
                  <li>• Consider their proposed delivery timeline and any price adjustments</li>
                  <li>• You can message freelancers to ask additional questions before assigning</li>
                  <li>• Once assigned, the project will move to "Active" status and payment will be requested</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectRequests;